import { useCallback } from "react";
import { useMutation } from "@apollo/client";

import {
  SendAdventDoorInviteMutation,
  SendAdventDoorInviteMutationVariables,
} from "../api/types";
import { SEND_ADVENT_DOOR_INVITE } from "utility/graphQl/mutation";

/**
 * Asks for a door's calendar invite. The payload's door shares the calendar
 * query's cache id, so the sheet reads the sent invite from the response.
 * Resolves with whether the learner already had it; rejects on any GraphQL
 * error or a response without a sent invite.
 */
export default function useSendAdventDoorInvite() {
  const [send] = useMutation<
    SendAdventDoorInviteMutation,
    SendAdventDoorInviteMutationVariables
  >(SEND_ADVENT_DOOR_INVITE);

  return useCallback(
    async (doorId: string): Promise<{ alreadySent: boolean }> => {
      const result = await send({ variables: { doorId } });
      const payload = result.data?.sendAdventDoorInvite;
      if (!payload?.ok) {
        throw new Error(`sendAdventDoorInvite was not ok for door ${doorId}`);
      }
      if (!payload.door?.invite?.sentAt) {
        throw new Error(
          `sendAdventDoorInvite returned no sent invite for door ${doorId}`
        );
      }
      return { alreadySent: payload.alreadySent };
    },
    [send]
  );
}
